import { lazy, Suspense, useEffect, useState } from 'react';
import { Link, Route, Routes, useLocation } from 'react-router-dom';

import { useAuth } from './lib/auth/AuthProvider';
import { trackPageview } from './lib/analytics';
import { setLocale, subscribeLocale, type Locale } from './lib/i18n';
import { AdminRoute, CompanyOnlyRoute } from './components/AdminRoute';
import { AnalyticsTags } from './components/AnalyticsTags';
import { ImpersonationBanner } from './components/ImpersonationBanner';
import { ProtectedLayout } from './components/ProtectedLayout';
import { ProtectedRoute, PublicOnlyRoute } from './components/ProtectedRoute';
import { RootRoute } from './components/RootRoute';
import { Spinner } from './components/ui';
// Embudo de anuncios (crítico) → eager, sin fetch de chunk extra al aterrizar.
import { LoginPage } from './pages/LoginPage';
import { RegisterPage } from './pages/RegisterPage';

/**
 * Code splitting (P3.1): todo lo que NO ve el tráfico de anuncios se carga por
 * demanda (React.lazy). Así el bundle inicial del landing/registro no arrastra el
 * panel de administración ni las pantallas internas de la app. `named()` adapta
 * los exports nombrados (la app no usa `export default`) al contrato de React.lazy.
 */
function named<M, K extends keyof M>(factory: () => Promise<M>, key: K) {
  return lazy(() =>
    factory().then((m) => ({ default: m[key] as unknown as React.ComponentType })),
  );
}

// --- Público secundario (legales / empresa / ayuda / verificación) ---
const AboutPage = named(() => import('./pages/public/AboutPage'), 'AboutPage');
const ContactPage = named(() => import('./pages/public/ContactPage'), 'ContactPage');
const SupportPage = named(() => import('./pages/public/SupportPage'), 'SupportPage');
const HelpIndexPage = named(() => import('./pages/public/HelpIndexPage'), 'HelpIndexPage');
const HelpArticlePage = named(() => import('./pages/public/HelpArticlePage'), 'HelpArticlePage');
const PrivacyPage = named(() => import('./pages/public/PrivacyPage'), 'PrivacyPage');
const TermsPage = named(() => import('./pages/public/TermsPage'), 'TermsPage');
const CookiesPage = named(() => import('./pages/public/CookiesPage'), 'CookiesPage');
const VerifyEmailPage = named(() => import('./pages/public/VerifyEmailPage'), 'VerifyEmailPage');
const ForgotPasswordPage = named(() => import('./pages/ForgotPasswordPage'), 'ForgotPasswordPage');
const ResetPasswordPage = named(() => import('./pages/ResetPasswordPage'), 'ResetPasswordPage');
const AcceptInvitationPage = named(() => import('./pages/AcceptInvitationPage'), 'AcceptInvitationPage');
const PublicSharePage = named(() => import('./pages/PublicSharePage'), 'PublicSharePage');

// --- Panel Super Admin (nunca lo ve el tráfico público) ---
const AdminLayout = named(() => import('./components/admin/AdminLayout'), 'AdminLayout');
const AdminDashboardPage = named(() => import('./pages/admin/AdminDashboardPage'), 'AdminDashboardPage');
const AdminCompaniesPage = named(() => import('./pages/admin/AdminCompaniesPage'), 'AdminCompaniesPage');
const FeedbackPage = named(() => import('./pages/admin/FeedbackPage'), 'FeedbackPage');
const VerticalsPage = named(() => import('./pages/admin/VerticalsPage'), 'VerticalsPage');
const FeaturesPage = named(() => import('./pages/admin/FeaturesPage'), 'FeaturesPage');
const PlansPage = named(() => import('./pages/admin/PlansPage'), 'PlansPage');
const AddonsPage = named(() => import('./pages/admin/AddonsPage'), 'AddonsPage');
const TemplatesPage = named(() => import('./pages/admin/TemplatesPage'), 'TemplatesPage');
const AdminPaymentsPage = named(() => import('./pages/admin/AdminPaymentsPage'), 'AdminPaymentsPage');
const PaymentMethodsPage = named(() => import('./pages/admin/PaymentMethodsPage'), 'PaymentMethodsPage');
const ExchangeRatesPage = named(() => import('./pages/admin/ExchangeRatesPage'), 'ExchangeRatesPage');
const AnnouncementsPage = named(() => import('./pages/admin/AnnouncementsPage'), 'AnnouncementsPage');
const EmailLogPage = named(() => import('./pages/admin/EmailLogPage'), 'EmailLogPage');
const AnalyticsPage = named(() => import('./pages/admin/AnalyticsPage'), 'AnalyticsPage');
const SettingsPage = named(() => import('./pages/admin/SettingsPage'), 'SettingsPage');
const SeoPage = named(() => import('./pages/admin/SeoPage'), 'SeoPage');
const SecurityPage = named(() => import('./pages/admin/SecurityPage'), 'SecurityPage');

// --- App interna de empresa (post-login) ---
const HomePage = named(() => import('./pages/HomePage'), 'HomePage');
const ProjectsPage = named(() => import('./pages/ProjectsPage'), 'ProjectsPage');
const NewProjectPage = named(() => import('./pages/NewProjectPage'), 'NewProjectPage');
const FolderView = named(() => import('./pages/FolderView'), 'FolderView');
const BoardPage = named(() => import('./pages/BoardPage'), 'BoardPage');
const MyTasksPage = named(() => import('./pages/MyTasksPage'), 'MyTasksPage');
const AgendaPage = named(() => import('./pages/AgendaPage'), 'AgendaPage');
const CreditsPage = named(() => import('./pages/CreditsPage'), 'CreditsPage');
const ReportsPage = named(() => import('./pages/ReportsPage'), 'ReportsPage');
const TeamPage = named(() => import('./pages/TeamPage'), 'TeamPage');
const BillingPage = named(() => import('./pages/BillingPage'), 'BillingPage');
const ProfilePage = named(() => import('./pages/ProfilePage'), 'ProfilePage');
const ConnectorsPage = named(() => import('./pages/ConnectorsPage'), 'ConnectorsPage');
const NotificationsPage = named(() => import('./pages/NotificationsPage'), 'NotificationsPage');
const PreferencesPage = named(() => import('./pages/PreferencesPage'), 'PreferencesPage');

/**
 * Rutas de la app (relativas al basename "/tembiapo").
 *
 *   /                    → pública: landing (home del sitio). Con sesión,
 *                          RootRoute redirige al dashboard (/inicio o /admin).
 *   /inicio              → protegida (dashboard de empresa). Sin sesión → /login.
 *   /login, /registro   → públicas (si ya hay sesión → redirige a /inicio).
 */
export default function App() {
  useLocaleSync();
  // Analítica web: registra una vista por cada cambio de ruta pública (el tracker
  // ignora la app interna y la app móvil). Dispara también en la carga inicial.
  const { pathname } = useLocation();
  useEffect(() => {
    trackPageview(pathname);
  }, [pathname]);
  return (
    <>
      {/* Etiquetas de SEM/analytics (GTM/GA4/Pixel) inyectadas una sola vez. */}
      <AnalyticsTags />
      {/* Banner de impersonación de soporte: fijo arriba de toda la app. */}
      <ImpersonationBanner />
      <Suspense fallback={<PageFallback />}>
        <Routes>
        {/* Home pública del sitio. No requiere auth. */}
        <Route path="/" element={<RootRoute />} />

        <Route element={<PublicOnlyRoute />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/registro" element={<RegisterPage />} />
          <Route path="/recuperar" element={<ForgotPasswordPage />} />
          <Route path="/restablecer/:token" element={<ResetPasswordPage />} />
        </Route>

        {/* Alta por invitación: pública (sin sesión). */}
        <Route path="/invite/:token" element={<AcceptInvitationPage />} />

        {/* Verificación de email por token: pública (funciona con o sin sesión). */}
        <Route path="/verificar-email/:token" element={<VerifyEmailPage />} />

        {/* Vista pública de solo lectura de un proyecto/carpeta compartido (sin auth). */}
        <Route path="/ver/:token" element={<PublicSharePage />} />

        {/* Páginas públicas del sitio (Empresa + Legales). Sin auth. */}
        <Route path="/sobre-nosotros" element={<AboutPage />} />
        <Route path="/contacto" element={<ContactPage />} />
        <Route path="/soporte" element={<SupportPage />} />
        <Route path="/ayuda" element={<HelpIndexPage />} />
        <Route path="/ayuda/:slug" element={<HelpArticlePage />} />
        <Route path="/privacidad" element={<PrivacyPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terminos" element={<TermsPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/cookies" element={<CookiesPage />} />

        {/* Panel Super Admin (rol SUPER_ADMIN) — su propio shell. */}
        <Route element={<AdminRoute />}>
          <Route path="admin" element={<AdminLayout />}>
            <Route index element={<AdminDashboardPage />} />
            <Route path="empresas" element={<AdminCompaniesPage />} />
            <Route path="feedback" element={<FeedbackPage />} />
            <Route path="verticales" element={<VerticalsPage />} />
            <Route path="features" element={<FeaturesPage />} />
            <Route path="planes" element={<PlansPage />} />
            <Route path="addons" element={<AddonsPage />} />
            <Route path="plantillas" element={<TemplatesPage />} />
            <Route path="pagos" element={<AdminPaymentsPage />} />
            <Route path="formas-de-pago" element={<PaymentMethodsPage />} />
            <Route path="tasas" element={<ExchangeRatesPage />} />
            <Route path="anuncios" element={<AnnouncementsPage />} />
            <Route path="correos" element={<EmailLogPage />} />
            <Route path="analitica" element={<AnalyticsPage />} />
            <Route path="configuracion" element={<SettingsPage />} />
            <Route path="seo" element={<SeoPage />} />
            <Route path="seguridad" element={<SecurityPage />} />
            <Route path="*" element={<NotFound />} />
          </Route>
        </Route>

        {/* App de empresa. Un super admin cae acá tras login → CompanyOnlyRoute
            lo reenvía a /admin. */}
        <Route element={<ProtectedRoute />}>
          <Route element={<CompanyOnlyRoute />}>
            <Route element={<ProtectedLayout />}>
              <Route path="inicio" element={<HomePage />} />
              <Route path="proyectos" element={<ProjectsPage />} />
              <Route path="proyectos/nuevo" element={<NewProjectPage />} />
              <Route path="proyectos/carpeta/:folderId" element={<FolderView />} />
              <Route path="proyectos/:id" element={<BoardPage />} />
              <Route path="mis-tareas" element={<MyTasksPage />} />
              <Route path="agenda" element={<AgendaPage />} />
              <Route path="creditos" element={<CreditsPage />} />
              <Route path="reportes" element={<ReportsPage />} />
              <Route path="equipo" element={<TeamPage />} />
              <Route path="facturacion" element={<BillingPage />} />
              <Route path="perfil" element={<ProfilePage />} />
              <Route path="conectores" element={<ConnectorsPage />} />
              <Route path="notificaciones" element={<NotificationsPage />} />
              <Route path="preferencias" element={<PreferencesPage />} />
              <Route path="*" element={<NotFound />} />
            </Route>
          </Route>
        </Route>
        </Routes>
      </Suspense>
    </>
  );
}

/** Fallback mientras carga un chunk lazy. */
function PageFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner />
    </div>
  );
}

/**
 * Reactividad del idioma a nivel de toda la app. `t()` lee un locale global no
 * reactivo, así que:
 *   1) nos suscribimos a `subscribeLocale` y forzamos un re-render de todo el
 *      árbol cuando el idioma cambia (selector del topbar, en caliente); y
 *   2) al cargar la sesión, inicializamos el idioma con el del perfil del usuario
 *      (para que respete su preferencia guardada entre sesiones).
 */
function useLocaleSync() {
  const { user } = useAuth();
  const [, force] = useState(0);

  useEffect(() => subscribeLocale(() => force((n) => n + 1)), []);

  useEffect(() => {
    const l = user?.locale;
    if (l === 'es' || l === 'pt') {
      setLocale(l as Locale);
    }
  }, [user?.locale]);
}

function NotFound() {
  return (
    <div className="py-16 text-center">
      <p className="font-mono text-sm text-muted">404</p>
      <h2 className="mt-2 font-display text-xl font-semibold text-ink">Página no encontrada</h2>
      <Link to="/" className="mt-4 inline-block text-brand underline hover:text-brand-2">
        Volver al inicio
      </Link>
    </div>
  );
}
